<script setup lang="ts">
import { compareStringsIgnoreCase, onElemVisible } from '@/ts/utils/utils';
import { getLangText } from '@/ts/utils/model-utils';
import {type FormInput} from './form-input.vue';
import { computed, inject, ref, nextTick, watch, onUnmounted } from 'vue';
import model from '@/ts/model';
import type { SelectElementListItem } from '@/ts/types/dom-types';
import LoadingSpinner from '../loading-spinner.vue';
import vPreloadImg from '@/ts/directives/v-preload-img';
import sendServerRequest from '@/ts/services/send-server-request.ts';
import type { LangTextPlaceholders } from '@/ts/types/lang-label-types.ts';

if(!inject('from-form-input-comp')) throw new Error("FormInputSelect should only be used within FormInput");

const ELEM_UI_MAX_HEIGHT = 500;

const input = defineModel<string | number | boolean | undefined>('input');
const props = defineProps<FormInput & {validationMessageParams: LangTextPlaceholders}>();

const elemWrapper = ref<HTMLElement>();
const elemUI = ref<HTMLElement>();

const dropdownShown = ref<boolean>(false);
const dataLoading = ref<boolean>(Boolean(props.list?.url));

const elemSearchBoxInput = ref<HTMLElement>();
const searchBoxInput = ref<string>('');
const searchBoxLoading = ref<boolean>(false);
const searchBoxResult = ref<SelectElementListItem[]>([]);
const searchFieldInput = ref<string>('');

const itemList = ref<any[] | undefined>();

watch(() => props.list?.data, value => {
    itemList.value = value;
}, {immediate: true});

const computedList = computed<SelectElementListItem[]>(() => {
    let list: SelectElementListItem[] = [];

    let resultList = itemList.value || searchBoxResult.value;

    if(resultList){
        for(let item of resultList){
            list.push({
                value: props.list?.keyName ? item[props.list.keyName] : item,
                text: props.list?.valLabel ? getLangText(item[props.list.valLabel]) : (props.list?.valName ? item[props.list.valName] : item),
                hidden: false,
                options: item.options,
            });
        }
    }

    if(props.renderDefaultOption){
        list.unshift({
            value: '',
            text: props.placeholder ? getLangText(props.placeholder, props.validationMessageParams) : (props.placeholderText || ''),
            hidden: !props.showDefaultOption,
        });
    }

    return list;
});

async function onShowDrowdown(){
    if(!elemWrapper.value) return;

    let prevOpened = dropdownShown.value;
    model.dispatch('HideAllSelectDropdowns');

    dropdownShown.value = !prevOpened;
    if(!dropdownShown.value) {
        if(elemUI.value){
            elemUI.value.style.position = '';
            elemUI.value.style.top = '';
            elemUI.value.style.left = '';
            elemUI.value.style.width = '';
            elemUI.value.style.maxHeight = '';
        }
        window.removeEventListener('resize', positionUI);
        return;
    }

    await nextTick();

    if(!elemUI.value) return;

    if(elemSearchBoxInput.value && !model.isMobile) elemSearchBoxInput.value.focus();

    let elemActiveOption = elemUI.value.querySelector('.option.active');
    if(elemActiveOption) elemActiveOption.scrollIntoView({behavior: 'instant', block: 'center'});

    positionUI();
    window.addEventListener('resize', positionUI);
}

onUnmounted(() => window.removeEventListener('resize', positionUI));

function positionUI(){
    if(!elemWrapper.value || !elemUI.value) return;

    let parentElem = elemWrapper.value.parentElement;
    if(!parentElem) return; 

    const box = parentElem.getBoundingClientRect();
    const top = box.top + box.height + window.scrollY;

    elemUI.value.style.position = 'absolute';
    elemUI.value.style.top = top + 'px';
    elemUI.value.style.left = box.left + 'px';
    elemUI.value.style.width = box.width + 'px';

    const wh = window.innerHeight; 

    if(!model.isMobile && top + ELEM_UI_MAX_HEIGHT > wh) elemUI.value.style.maxHeight = (wh - top - 20) + 'px';
    else elemUI.value.style.maxHeight = ELEM_UI_MAX_HEIGHT + 'px';
}

function onOptionClick(item: SelectElementListItem){
    input.value = item.value;
    dropdownShown.value = false;
}

model.on('HideAllSelectDropdowns', () => {
    dropdownShown.value = false;
});

const searchBoxCallback = props.list?.searchBoxRequest;
if(typeof searchBoxCallback === 'function'){
    let t = 0;
    let lastValue = '';
    watch(() => searchBoxInput.value, value => {
        clearTimeout(t);
        lastValue = value;
        searchBoxLoading.value = true;

        if(!value) {
            searchBoxResult.value = [];
            searchBoxLoading.value = false;
            return;
        }

        t = setTimeout(async () => {
            if(typeof searchBoxCallback !== 'function' || !props.list) return;

            let result = await searchBoxCallback(value);

            if(value !== lastValue) return;

            searchBoxResult.value = result;
            searchBoxLoading.value = false;
        }, 1000);
    });
}

watch(() => computedList.value, (value) => {
    if(value.filter(x => !x.hidden).length > 0) return;
    input.value = value.find(x => x.hidden)?.value;
});

const vDataLoading = {
    mounted(element: HTMLElement){
        const url = props.list?.url;
        if(!url) return;

        const observer = onElemVisible(element, () => {
            dataLoading.value = true;
            sendServerRequest(url, undefined, {global: false}).then(async data => {
                if(!data) return;

                const resp = await data.json();
                itemList.value = resp;
                dataLoading.value = false;
            });
            observer.disconnect();
        });
    },
};

watch(() => searchFieldInput.value, value => {
    for(const item of computedList.value){
        if(!value || !item.text){
            item.filteredOut = false;
            continue;
        }

        item.filteredOut = !compareStringsIgnoreCase(value, item.text);
    }
});
</script>

<template>
    <div class="select-wrapper" ref="elemWrapper">
        <template v-if="dataLoading">
            <div v-data-loading class="pending-select-element-data">
                <loading-spinner />
            </div>
        </template>
        <template v-else>
            <select v-model="input" :disabled="disabled" @click.prevent.stop="onShowDrowdown" @mousedown.prevent.stop>
                <option v-for="item in computedList" :value="item.value" :hidden="item.hidden" :key="item.value">{{ item.text }}</option>
            </select>
            <template v-if="dropdownShown && list">
                <teleport to="body">
                    <div class="ui-overlay" @click="onShowDrowdown" v-mobile></div>
                    <div class="ui" @click.prevent.stop ref="elemUI" :style="selectDropdownStyle">
                        <template v-if="list.searchField">
                            <div class="search-box">
                                <input type="text" v-model="searchFieldInput" :placeholder="getLangText('SearchLabel')">
                            </div>
                        </template>
                        <template v-if="!computedList.filter(x => !x.filteredOut).length">
                            <p class="no-data">{{ getLangText('NoDataFoundLabel') }}</p>
                        </template>
                        <template v-if="list.searchBoxRequest">
                            <div class="search-box">
                                <input type="text" v-model="searchBoxInput" :placeholder="getLangText('SearchLabel')" ref="elemSearchBoxInput">
                            </div>
                        </template>
                        <template v-if="searchBoxLoading">
                            <loading-spinner />
                        </template>
                        <template v-else>
                            <template v-if="list.searchBoxRequest && !searchBoxInput && list.searchBoxEmptyInputLabel">
                                <p class="no-data">{{ getLangText(list.searchBoxEmptyInputLabel) }}</p>
                            </template>
                            <template v-else-if="!computedList.filter(x => !x.hidden).length">
                                <p class="no-data">{{ getLangText('NoDataFoundLabel') }}</p>
                            </template>
                            <template v-else>
                                <template v-for="item in computedList" :key="item.value">
                                    <template v-if="!item.hidden && !item.filteredOut">
                                        <div 
                                            class="option" 
                                            :data-value="item.value" 
                                            :class="{
                                                'active': item.value == input,
                                                'has-img-right': list?.searchBoxImageRight,
                                            }" 
                                            @click.prevent.stop="onOptionClick(item)"
                                        >
                                            <template v-if="list.getImageLeft?.(item)">
                                                <div class="img-left">
                                                    <img v-preload-img="list.getImageLeft(item)">
                                                </div>
                                            </template>
                                            <span class="text">{{ item.text }}</span>
                                            <template v-if="list.searchBoxImageRight">
                                                <div class="img-right" :style="{backgroundImage: `url(${list.searchBoxImageRight(item)})`}"></div>
                                            </template>
                                        </div>
                                    </template>
                                </template>
                            </template>
                        </template>
                    </div>
                </teleport>
            </template>
        </template>
    </div>
</template>

<style lang="scss" scoped>
@use '@/scss/shared';
.select-wrapper{
    display: relative;
}
.ui{
    position: fixed;
    top: 0;
    left: 0;
    background-color: #000000;
    z-index: 1;
    max-width: 400px;
    width: 100%;
    max-height: 100dvh;
    border-radius: 5px;
    overflow-y: auto;
    color: #fff;
    z-index: 605;
    &-overlay{
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 600;
    }
    @include shared.site() using($site-key){
        border: 2px solid shared.theme-color("comp3_3", $site-key);
    }
    & > .option{
        display: flex;
        padding: 7px;
        transition: background-color .25s;
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
        & > .text{
            white-space: nowrap;
        }
        &.has-img-right{
            justify-content: space-between;
        }
        &:hover,
        &.active{
            @include shared.site() using($site-key){
                background-color: shared.theme-color("comp3_3", $site-key);
            }
        }
        @include shared.site() using($site-key){
            border-bottom: 1px solid shared.theme-color("comp3_3", $site-key);
        }
        & > .img-right{
            flex: 0 0 100px;
            background-repeat: no-repeat;
            background-position: center right;
            background-size: contain;
        }
        & > .img-left{
            flex: 0 0 20px;
            background-repeat: no-repeat;
            background-position: center left;
            background-size: contain;
            margin-right: 10px;
            & > img{
                display: block;
                width: 100%;
                animation-name: img-display;
                animation-duration: .25s;
                animation-fill-mode: forwards;
                @keyframes img-display {
                    from{
                        opacity: 0;
                    }
                    to{
                        opacity: 1;
                    }
                }
            }
        }
    }
    & > .no-data{
        font-weight: 700;
        text-align: center;
    }
    & > .search-box{
        width:100%;
        padding: 5px;
        box-sizing: border-box;
        & > input{
            width: 100%;
            box-sizing: border-box;
            padding: 5px;
            outline: none;
            @include shared.site() using($site-key){
                border: 2px solid shared.theme-color("comp3_3", $site-key);
            }
        }
    }
}
</style>